import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCertificateStore } from '@store'
import { Save, AlertCircle } from 'lucide-react'
import { Modal, Button, Alert, Input, Select, Textarea } from '@components/common'
import {
	Award,
	Shield,
	CheckCircle,
	Lock,
	UserCheck,
	UserLock,
	Leaf,
	Heart,
	Hospital,
	CreditCard,
	Flag,
	CloudUpload,
	Pill,
	RefreshCw,
	Cloud,
} from 'lucide-react'
import './CertificateForm.css'

/**
 * Certificate Form Validation Schema
 */
const certificateSchema = z.object({
	title: z
		.string()
		.min(3, 'Title must be at least 3 characters')
		.max(100, 'Title cannot exceed 100 characters'),
	slug: z
		.string()
		.min(3, 'Slug must be at least 3 characters')
		.max(100, 'Slug cannot exceed 100 characters')
		.regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
	description: z
		.string()
		.max(1000, 'Description cannot exceed 1000 characters')
		.optional()
		.or(z.literal('')),
	icon: z.string().max(50, 'Icon name cannot exceed 50 characters').optional().or(z.literal('')),
	status: z.enum(['active', 'archived'], {
		errorMap: () => ({ message: 'Status must be active or archived' }),
	}),
})

/**
 * Certificate Form Component
 *
 * Universal form for creating and editing certificates
 *
 * Features:
 * - React Hook Form for form management
 * - Zod for validation
 * - Auto-generate slug from title
 * - Real-time validation
 * - Loading states
 * - Error handling
 * - Accessible
 *
 * @param {string} mode - 'create' or 'edit'
 * @param {Function} onClose - Close modal callback
 */
const CertificateForm = ({ mode = 'create', onClose }) => {
	const { selectedCertificate, createCertificate, updateCertificate } = useCertificateStore()

	const [isSubmitting, setIsSubmitting] = useState(false)
	const [submitError, setSubmitError] = useState(null)

	const isEditMode = mode === 'edit'

	// Initialize form with React Hook Form + Zod
	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
	} = useForm({
		resolver: zodResolver(certificateSchema),
		mode: 'onChange',
		defaultValues: {
			title: selectedCertificate?.title || '',
			slug: selectedCertificate?.slug || '',
			description: selectedCertificate?.description || '',
			icon: selectedCertificate?.icon || 'award',
			status: selectedCertificate?.status || 'active',
		},
	})

	const titleValue = watch('title')

	// Auto-generate slug from title
	useEffect(() => {
		if (!isEditMode && titleValue) {
			const generatedSlug = titleValue
				.toLowerCase()
				.replace(/[^a-z0-9\s-]/g, '')
				.replace(/\s+/g, '-')
				.replace(/-+/g, '-')
				.trim()

			setValue('slug', generatedSlug)
		}
	}, [titleValue, isEditMode, setValue])

	// Handle form submission
	const onSubmit = async (data) => {
		setIsSubmitting(true)
		setSubmitError(null)

		try {
			const formData = {
				title: data.title,
				slug: data.slug,
				description: data.description || '',
				icon: data.icon || 'award',
				status: data.status,
			}

			if (isEditMode) {
				await updateCertificate(selectedCertificate.id, formData)
			} else {
				await createCertificate(formData)
			}

			onClose()
		} catch (error) {
			console.error('Form submission error:', error)
			setSubmitError(error.message || 'Failed to save certificate')
		} finally {
			setIsSubmitting(false)
		}
	}

	// Icon mapping with Lucide components
	const iconOptions = [
		{ value: 'award', label: 'Award', Icon: Award },
		{ value: 'shield-alt', label: 'Shield', Icon: Shield },
		{ value: 'check-circle', label: 'Check Circle', Icon: CheckCircle },
		{ value: 'lock', label: 'Lock', Icon: Lock },
		{ value: 'user-shield', label: 'User Shield', Icon: UserCheck },
		{ value: 'user-lock', label: 'User Lock', Icon: UserLock },
		{ value: 'leaf', label: 'Leaf', Icon: Leaf },
		{ value: 'heartbeat', label: 'Heartbeat', Icon: Heart },
		{ value: 'hospital', label: 'Hospital', Icon: Hospital },
		{ value: 'credit-card', label: 'Credit Card', Icon: CreditCard },
		{ value: 'flag-usa', label: 'Flag', Icon: Flag },
		{ value: 'cloud-upload-alt', label: 'Cloud Upload', Icon: CloudUpload },
		{ value: 'cloud', label: 'Cloud', Icon: Cloud },
		{ value: 'cloud-lock', label: 'Cloud Lock', Icon: Cloud },
		{ value: 'pills', label: 'Pills', Icon: Pill },
		{ value: 'sync-alt', label: 'Sync', Icon: RefreshCw },
		{ value: 'file-signature', label: 'File Signature', Icon: Award },
		{ value: 'hard-hat', label: 'Hard Hat', Icon: Shield },
	]

	const modalTitle = isEditMode ? 'Edit Certificate' : 'Create New Certificate'

	const footer = (
		<>
			<Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
				Cancel
			</Button>
			<Button
				variant="primary"
				onClick={handleSubmit(onSubmit)}
				loading={isSubmitting}
				disabled={!isDirty && isEditMode}
			>
				<Save size={18} />
				<span>{isEditMode ? 'Update Certificate' : 'Create Certificate'}</span>
			</Button>
		</>
	)

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			title={modalTitle}
			size="large"
			footer={footer}
			closeOnEscape={false}
			closeOnBackdrop={false}
		>
			<form className="certificate-form" onSubmit={handleSubmit(onSubmit)}>
				{submitError && (
					<Alert variant="error" message={submitError} dismissible onClose={() => setSubmitError(null)} />
				)}

				{/* Basic Information */}
				<div className="certificate-form__section">
					<h3 className="certificate-form__section-title">Basic Information</h3>

					<div className="certificate-form__grid">
						<div className="form-group">
							<Input
								type="text"
								label="Certificate Title"
								placeholder="e.g., ISO 27001:2013"
								required
								error={errors.title?.message}
								disabled={isSubmitting}
								{...register('title')}
							/>
						</div>

						<div className="form-group">
							<Input
								type="text"
								label="URL Slug"
								placeholder="e.g., iso-27001-2013"
								required
								error={errors.slug?.message}
								disabled={isSubmitting || isEditMode}
								{...register('slug')}
							/>
							{!isEditMode && <p className="form-hint">Auto-generated from title</p>}
						</div>
					</div>

					<div className="certificate-form__full">
						<div className="form-group">
							<Textarea
								label="Description"
								placeholder="Describe the certification, its purpose, and requirements..."
								rows={4}
								maxLength={1000}
								showCount
								error={errors.description?.message}
								disabled={isSubmitting}
								{...register('description')}
							/>
						</div>
					</div>
				</div>

				{/* Visual & Status */}
				<div className="certificate-form__section">
					<h3 className="certificate-form__section-title">Visual & Status</h3>

					<div className="certificate-form__grid">
						<div className="form-group">
							<label className="form-label">Icon</label>
							<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
								{watch('icon') && iconOptions.find((opt) => opt.value === watch('icon'))?.Icon && (
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											padding: '0.5rem',
											background: '#f3f4f6',
											borderRadius: '6px',
										}}
									>
										{React.createElement(
											iconOptions.find((opt) => opt.value === watch('icon')).Icon,
											{ size: 20 }
										)}
									</div>
								)}
								<div style={{ flex: 1 }}>
									<Select
										placeholder="Select icon"
										options={iconOptions.map((option) => ({
											value: option.value,
											label: option.label
										}))}
										error={errors.icon?.message}
										disabled={isSubmitting}
										{...register('icon')}
									/>
								</div>
							</div>
						</div>

						<div className="form-group">
							<Select
								label="Status"
								required
								options={[
									{ value: 'active', label: 'Active' },
									{ value: 'archived', label: 'Archived' }
								]}
								error={errors.status?.message}
								disabled={isSubmitting}
								{...register('status')}
							/>
						</div>
					</div>
				</div>
			</form>
		</Modal>
	)
}

export default CertificateForm
